import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { EventNote } from '@mui/icons-material';
import DialogHeader from '../Common/DialogHeader';
import { useAuth } from '../../contexts/AuthContext';
import DynamicFormRenderer from '../Forms/DynamicFormRenderer';
import providerFormService from '../../services/providerFormService';
import packageService from '../../services/packageService';
import weddingEventService from '../../services/weddingEventService';
import { trackProviderEvent, ANALYTICS_EVENTS } from '../../services/analyticsService';
import { formatPackagePrice } from '../../utils/currency';

const formatPrice = formatPackagePrice;

const OfferRequestForm = ({ open, onClose, provider, onSuccess }) => {
  const { user, isAuthenticated } = useAuth();

  const [form, setForm] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [formError, setFormError] = useState(null);

  const [fieldValues, setFieldValues] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [userInitialValues, setUserInitialValues] = useState({});

  const [weddingEvents, setWeddingEvents] = useState([]);
  const [selectedWeddingEventId, setSelectedWeddingEventId] = useState('');

  // Form és csomagok betöltése
  useEffect(() => {
    if (!open || !provider?.id) return;

    setLoadingForm(true);
    setFormError(null);
    setForm(null);
    setFieldValues({});
    setIsFormValid(false);
    setApiError(null);
    setShowValidation(false);
    setPackages([]);
    setSelectedPackageId('');
    setUserInitialValues({});
    setWeddingEvents([]);
    setSelectedWeddingEventId('');

    const formPromise = providerFormService.getActiveProviderFormForPublic(provider.id)
      .then(response => {
        if (response.success) {
          const formData = response.data;
          setForm(formData);
          if (user) {
            const mapping = {
              contactName: [user.firstName, user.lastName].filter(Boolean).join(' '),
              contactEmail: user.email || '',
              contactPhone: user.phone || '',
            };
            const prefill = {};
            (formData.fields || []).forEach(field => {
              if (field.systemKey && mapping[field.systemKey]) {
                prefill[field.id] = mapping[field.systemKey];
              }
            });
            setUserInitialValues(prefill);
          }
        } else {
          setFormError(response.message || 'A form betöltése sikertelen.');
        }
      })
      .catch(() => setFormError('Hiba a form betöltése során.'));

    const packagesPromise = packageService.getPackagesByProvider(provider.id)
      .then(res => {
        if (res.success && res.data.packages.length > 0) {
          setPackages(res.data.packages);
        }
      })
      .catch(() => {});

    const eventsPromise = isAuthenticated
      ? weddingEventService.getEvents()
          .then(res => {
            if (res.success) {
              const active = (res.data || []).filter(e => ['PLANNING', 'CONFIRMED'].includes(e.status));
              setWeddingEvents(active);
            }
          })
          .catch(() => {})
      : Promise.resolve();

    Promise.all([formPromise, packagesPromise, eventsPromise]).finally(() => setLoadingForm(false));
  }, [open, provider?.id]);

  const handleFormChange = useCallback((values, valid) => {
    setFieldValues(values);
    setIsFormValid(valid);
  }, []);

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setApiError('Az ajánlatkéréshez be kell jelentkeznie.');
      return;
    }
    if (!form) return;
    if (!isFormValid) {
      setShowValidation(true);
      setApiError('Kérjük, töltse ki a kötelező mezőket.');
      return;
    }

    setSubmitting(true);
    setApiError(null);

    try {
      const fieldResponses = Object.entries(fieldValues).map(([fieldId, value]) => ({
        fieldId: parseInt(fieldId),
        value: value !== null && value !== undefined ? String(value) : '',
      }));

      const response = await providerFormService.createBookingWithForm({
        providerId: provider.id,
        formId: form.id,
        fieldResponses,
        selectedPackageId: selectedPackageId || null,
        weddingEventId: selectedWeddingEventId && selectedWeddingEventId !== 'skip' ? parseInt(selectedWeddingEventId) : null,
        skipBudgetSync: selectedWeddingEventId === 'skip',
      });

      if (response.success) {
        trackProviderEvent(ANALYTICS_EVENTS.OFFER_REQUEST, provider.id);
        onSuccess(response.data);
        onClose();
      } else {
        setApiError(response.message || 'Az ajánlatkérés sikertelen.');
      }
    } catch (err) {
      setApiError(err.message || 'Hiba történt az ajánlatkérés során.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={<EventNote />}
          title="Ajánlatkérés"
          subtitle={provider?.businessName}
        />
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: 3,
          mt: 2.5,
          overflowX: 'hidden',
        }}
      >
        {apiError && (
          <Alert severity="error" sx={{ mb: 2.5 }}>{apiError}</Alert>
        )}

        {loadingForm && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 6 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Betöltés…</Typography>
          </Box>
        )}

        {formError && !loadingForm && (
          <Alert severity="error">{formError}</Alert>
        )}

        {!loadingForm && form && (
          <>
            {packages.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, color: 'primary.main', whiteSpace: 'nowrap' }}
                  >
                    Csomag
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
                <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel shrink>Csomag kiválasztása (opcionális)</InputLabel>
                <Select
                  value={selectedPackageId}
                  onChange={(e) => setSelectedPackageId(e.target.value)}
                  label="Csomag kiválasztása (opcionális)"
                  displayEmpty
                  notched
                >
                  <MenuItem value=""><em>Nincs csomag kiválasztva</em></MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name} – {formatPrice(pkg.price, pkg.priceUnit)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              </>
            )}

            {weddingEvents.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: packages.length > 0 ? 0 : 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, color: 'primary.main', whiteSpace: 'nowrap' }}
                  >
                    Esküvő esemény
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel shrink>Melyik eseményhez tartozik?</InputLabel>
                  <Select
                    value={selectedWeddingEventId}
                    onChange={(e) => setSelectedWeddingEventId(e.target.value)}
                    label="Melyik eseményhez tartozik?"
                    displayEmpty
                    notched
                  >
                    <MenuItem value=""><em>Automatikus hozzárendelés</em></MenuItem>
                    <MenuItem value="skip"><em>Ne rendelje eseményhez</em></MenuItem>
                    {weddingEvents.map((event) => (
                      <MenuItem key={event.id} value={event.id}>
                        {event.name}
                        {event.eventDate && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({new Date(event.eventDate).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })})
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <DynamicFormRenderer
              form={form}
              initialValues={userInitialValues}
              onFormChange={handleFormChange}
              showTitle={false}
              showErrors={showValidation}
            />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.5,
          py: 2.5,
          gap: 1.5,
          borderTop: '1px solid',
          borderTopColor: 'divider',
          bgcolor: 'grey.50',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Button onClick={onClose} disabled={submitting} variant="text" color="inherit">
          Mégse
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingForm || !!formError || !isFormValid}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          sx={{ ml: 'auto', px: 3 }}
        >
          Ajánlatkérés elküldése
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferRequestForm;
